/**
 * AttendanceTable.js
 * Live attendance log — shows registered name when available,
 * with a quick "Register" button for unknown UIDs.
 */

import React, { useState, useMemo } from "react";
import { exportToCSV } from "../utils/exportCSV";

// ── Status badge ─────────────────────────────────────────────
function StatusBadge({ status }) {
  if (status === "present") {
    return (
      <span className="badge-present">
        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
        Present
      </span>
    );
  }
  return (
    <span className="badge-denied">
      <span className="w-1.5 h-1.5 rounded-full bg-red-500" />
      Denied
    </span>
  );
}

// ── Empty state ───────────────────────────────────────────────
function EmptyState({ connected }) {
  return (
    <div className="flex flex-col items-center justify-center py-20 gap-4 text-center">
      <div className="w-16 h-16 rounded-2xl bg-slate-100 flex items-center justify-center text-3xl">
        🪪
      </div>
      <div>
        <p className="font-semibold text-slate-600">No scans yet</p>
        <p className="text-sm text-slate-400 mt-1">
          {connected
            ? "Waiting for RFID card scans from ESP8266..."
            : "Connecting to MQTT broker..."}
        </p>
      </div>
    </div>
  );
}

// ── Person cell ───────────────────────────────────────────────
function PersonCell({ uid, lookup, onRegister }) {
  const person = lookup(uid);
  if (person) {
    return (
      <div className="flex items-center gap-2">
        <div className="w-7 h-7 rounded-full bg-brand-100 flex items-center
                        justify-center text-brand-700 text-xs font-bold flex-shrink-0">
          {person.name.charAt(0).toUpperCase()}
        </div>
        <div>
          <p className="text-sm font-semibold text-slate-800 leading-tight">
            {person.name}
          </p>
          <p className="text-xs text-slate-400">{person.studentId}</p>
        </div>
      </div>
    );
  }
  return (
    <button
      onClick={() => onRegister(uid)}
      className="inline-flex items-center gap-1 text-xs text-brand-600
                 hover:text-brand-800 font-semibold hover:underline
                 transition-colors"
    >
      <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none"
           stroke="currentColor" strokeWidth="2.5">
        <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
      </svg>
      Register
    </button>
  );
}

export default function AttendanceTable({ records, onClear, connected, lookup, onRegister }) {
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");

  const filtered = useMemo(() => {
    return records.filter((r) => {
      const matchFilter = filter === "all" || r.status === filter;
      const person = lookup(r.uid);
      const matchSearch =
        search === "" ||
        r.uid.toLowerCase().includes(search.toLowerCase()) ||
        r.timestamp.includes(search) ||
        (person && person.name.toLowerCase().includes(search.toLowerCase())) ||
        (person && person.studentId.toLowerCase().includes(search.toLowerCase()));
      return matchFilter && matchSearch;
    });
  }, [records, filter, search, lookup]);

  // Enrich records for CSV export
  const enriched = filtered.map((r) => {
    const p = lookup(r.uid);
    return { ...r, name: p?.name || "", studentId: p?.studentId || "" };
  });

  return (
    <div className="glass-card overflow-hidden">

      {/* ── Card header ─────────────────────────────────── */}
      <div className="px-6 py-4 border-b border-slate-100 flex flex-wrap items-center
                      justify-between gap-3">
        <div>
          <h2 className="text-base font-bold text-slate-800">Live Attendance Log</h2>
          <p className="text-xs text-slate-400 mt-0.5">
            Real-time updates · newest first
          </p>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={() => exportToCSV(enriched)} className="btn-ghost text-xs">
            <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none"
                 stroke="currentColor" strokeWidth="2.2">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
              <polyline points="7 10 12 15 17 10"/>
              <line x1="12" y1="15" x2="12" y2="3"/>
            </svg>
            Export CSV
          </button>
          <button onClick={onClear}
                  className="btn-ghost text-xs text-red-500 border-red-200 hover:bg-red-50">
            <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none"
                 stroke="currentColor" strokeWidth="2.2">
              <polyline points="3 6 5 6 21 6"/>
              <path d="M19 6l-1 14H6L5 6"/>
              <path d="M10 11v6M14 11v6"/>
              <path d="M9 6V4h6v2"/>
            </svg>
            Clear
          </button>
        </div>
      </div>

      {/* ── Filter + search ──────────────────────────────── */}
      <div className="px-6 py-3 border-b border-slate-100 flex flex-wrap items-center
                      gap-3 bg-slate-50/50">
        <div className="flex gap-1.5 p-1 bg-white rounded-xl border border-slate-200 shadow-sm">
          {["all", "present", "denied"].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-3 py-1 rounded-lg text-xs font-semibold capitalize transition-all ${
                filter === f
                  ? "bg-brand-600 text-white shadow-sm"
                  : "text-slate-500 hover:bg-slate-100"
              }`}
            >
              {f}
            </button>
          ))}
        </div>

        <div className="flex-1 min-w-[180px] relative">
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400"
               viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
            <circle cx="11" cy="11" r="8"/>
            <line x1="21" y1="21" x2="16.65" y2="16.65"/>
          </svg>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name, UID, or ID..."
            className="w-full pl-8 pr-3 py-1.5 rounded-xl text-xs border border-slate-200
                       bg-white focus:outline-none focus:ring-2 focus:ring-brand-300
                       placeholder-slate-400 text-slate-700"
          />
        </div>

        <span className="text-xs text-slate-400 font-medium ml-auto">
          {filtered.length} record{filtered.length !== 1 ? "s" : ""}
        </span>
      </div>

      {/* ── Table ─────────────────────────────────────────── */}
      <div className="overflow-x-auto">
        {filtered.length === 0 ? (
          <EmptyState connected={connected} />
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50/80 border-b border-slate-100">
                {["#", "Person", "Card UID", "Timestamp", "Status"].map((h) => (
                  <th key={h} className="px-5 py-3 text-left text-xs font-semibold
                                          text-slate-500 uppercase tracking-wide whitespace-nowrap">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.map((row, idx) => (
                <tr key={row.id}
                    className="row-enter hover:bg-brand-50/40 transition-colors duration-150">
                  <td className="px-5 py-3.5 text-xs text-slate-400 font-medium">
                    {filtered.length - idx}
                  </td>
                  <td className="px-5 py-3.5">
                    <PersonCell uid={row.uid} lookup={lookup} onRegister={onRegister} />
                  </td>
                  <td className="px-5 py-3.5">
                    <span className="font-mono text-xs font-semibold text-slate-700
                                     bg-slate-100 px-2.5 py-0.5 rounded-lg tracking-wider">
                      {row.uid}
                    </span>
                  </td>
                  <td className="px-5 py-3.5 text-slate-500 text-xs whitespace-nowrap">
                    <div className="flex items-center gap-1.5">
                      <svg className="w-3.5 h-3.5 text-slate-400 flex-shrink-0"
                           viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="10"/>
                        <polyline points="12 6 12 12 16 14"/>
                      </svg>
                      {row.timestamp}
                    </div>
                  </td>
                  <td className="px-5 py-3.5">
                    <StatusBadge status={row.status} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* ── Footer ────────────────────────────────────────── */}
      {filtered.length > 0 && (
        <div className="px-6 py-3 border-t border-slate-100 bg-slate-50/50
                        flex items-center justify-between text-xs text-slate-400">
          <span>Showing {filtered.length} of {records.length} total records</span>
          <span className="font-medium text-brand-500">Live · Auto-updating</span>
        </div>
      )}
    </div>
  );
}
